import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Star, Send } from 'lucide-react';
import { format } from 'date-fns';

const ReviewsSection = ({
  reviews,
  user,
  userHasBooking,
  userHasReviewed,
  showReviewForm,
  setShowReviewForm,
  reviewRating,
  setReviewRating,
  reviewComment,
  setReviewComment,
  submittingReview,
  onSubmitReview
}) => {
  const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (interactive) {
        stars.push(
          <button
            key={i}
            type="button"
            onClick={() => setReviewRating(i)}
            className="focus:outline-none"
          >
            <Star
              className={`${size} ${i <= reviewRating ? 'fill-amber-400 text-amber-400' : 'text-neutral-600'} transition-colors`}
            />
          </button>
        );
      } else {
        stars.push(
          <Star
            key={i}
            className={`${size} ${i <= rating ? 'fill-amber-400 text-amber-400' : 'text-neutral-600'}`}
          />
        );
      }
    }
    return stars;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mt-16"
    >
      <div className="flex justify-between items-center mb-8">
        <h2 className="font-heading text-3xl font-bold">
          REVIEWS <span className="text-neutral-500">({reviews.length})</span>
        </h2>
        {user && userHasBooking && !userHasReviewed && (
          <Button
            onClick={() => setShowReviewForm(!showReviewForm)}
            className="bg-primary hover:bg-primary-hover"
            data-testid="write-review-btn"
          >
            <Star className="w-4 h-4 mr-2" />
            Write a Review
          </Button>
        )}
      </div>

      {/* Review Form */}
      {showReviewForm && (
        <motion.form
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          onSubmit={onSubmitReview}
          className="bg-background-paper border border-white/10 rounded-sm p-6 mb-8"
        >
          <h3 className="font-heading text-xl font-bold mb-4">Share Your Experience</h3>
          
          <div className="mb-4">
            <label className="text-sm text-neutral-400 mb-2 block">Rating</label>
            <div className="flex gap-1">
              {renderStars(reviewRating, true, 'w-8 h-8')}
            </div>
          </div>

          <div className="mb-4">
            <label className="text-sm text-neutral-400 mb-2 block">Your Review</label>
            <Textarea
              value={reviewComment}
              onChange={(e) => setReviewComment(e.target.value)}
              placeholder="Share your thoughts about this class..."
              className="bg-background border-white/10 min-h-[100px]"
              data-testid="review-comment-input"
            />
          </div>

          <div className="flex gap-4">
            <Button
              type="submit"
              disabled={submittingReview}
              className="bg-primary hover:bg-primary-hover"
              data-testid="submit-review-btn"
            >
              {submittingReview ? 'Submitting...' : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Review
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowReviewForm(false)}
            >
              Cancel
            </Button>
          </div>
        </motion.form>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div className="bg-background-paper border border-white/10 rounded-sm p-8 text-center">
          <Star className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
          <p className="text-neutral-400">No reviews yet</p>
          <p className="text-sm text-neutral-500 mt-2">Be the first to review this class!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review, index) => (
            <motion.div
              key={review.review_id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-background-paper border border-white/10 rounded-sm p-6"
              data-testid={`review-card-${index}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-bold text-lg">{review.user_name}</p>
                  <div className="flex gap-1 mt-1">
                    {renderStars(review.rating)}
                  </div>
                </div>
                <span className="text-sm text-neutral-500">
                  {format(new Date(review.created_at), 'MMM dd, yyyy')}
                </span>
              </div>
              <p className="text-neutral-300">{review.comment}</p>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default ReviewsSection;
